import React, { useEffect, useState } from "react";
import { Users as UsersIcon, GraduationCap, FileCheck, Award } from "lucide-react";
import api from "../api";
import AvailabilityPill from "../components/AvailabilityPill";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

function Stat({ label, value }) {
  return <div className="rounded-xl border border-slate-100 p-4"><div className="text-2xl font-extrabold text-violet-600">{value}</div><div className="text-xs uppercase tracking-wide text-slate-400">{label}</div></div>;
}

export default function SupervisorApplicants() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ assigned: 0, client_ready: 0, certified: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/supervisor/applicants").then((r) => setRows(r.data)).catch(() => setRows([])),
      api.get("/supervisor/summary").then((r) => setSummary(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="supervisor-applicants">
      <div>
        <h1 className="text-xl font-bold text-slate-900">My Applicants</h1>
        <p className="text-sm text-slate-500">Contractors assigned to you. This list is filtered automatically to your assignments.</p>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Stat label="Assigned to me" value={summary.assigned} />
        <Stat label="Certified" value={summary.certified} />
        <Stat label="Client ready" value={summary.client_ready} />
      </div>
      <Card>
        {loading ? <div className="py-8 text-center text-sm text-slate-400">Loading…</div> : rows.length === 0 ? (
          <div className="py-10 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100"><UsersIcon className="h-6 w-6 text-slate-400" /></div>
            <p className="mt-3 text-sm text-slate-500">No applicants are assigned to you yet. An administrator assigns applicants from People management.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2 pr-4">Applicant</th><th className="py-2 pr-4">Onboarding</th><th className="py-2 pr-4">Documents</th><th className="py-2 pr-4">Availability</th><th className="py-2 pr-4">Assessment</th><th className="py-2 pr-4">Status</th>
              </tr></thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} className="border-b border-slate-50" data-testid={`sup-applicant-${c.id}`}>
                    <td className="py-3 pr-4"><div className="font-medium text-slate-800">{c.name}</div><div className="text-xs text-slate-400">{c.email}</div></td>
                    <td className="py-3 pr-4 text-slate-600">{c.onboarding_percent}%</td>
                    <td className="py-3 pr-4 text-slate-600"><span className="inline-flex items-center gap-1"><FileCheck className="h-3.5 w-3.5 text-emerald-500" /> {c.docs_on_file ?? c.docs_approved}/{c.docs_total}</span></td>
                    <td className="py-3 pr-4" data-testid={`sup-availability-${c.id}`}><AvailabilityPill status={c.availability_status} /></td>
                    <td className="py-3 pr-4 text-slate-600">{c.assessment_score != null ? `${c.assessment_score}%` : "—"}</td>
                    <td className="py-3 pr-4">
                      {c.client_ready ? <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"><Award className="h-3 w-3" /> Client Ready</span>
                        : c.certified ? <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700">Certified</span>
                        : <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-500"><GraduationCap className="h-3 w-3" /> In progress</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
